import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers'
import {
    ApartmentContract,
    ApartmentContractType,
    ContractPaymentTerm,
} from '../models/Company/types'

const QUERY_KEY = ['companyContracts']
const ANALYTICS_QUERY_KEY = ['contractAnalytics']

export interface ContractAnalytics {
    total: number
    active: number
    ending_soon: number
    auto_renew_notice: number
    expired: number
    no_dates: number
}

export const useCompanyContracts = () => {
    const queryClient = useQueryClient()
    const [processingIds, setProcessingIds] = useState<Set<number>>(new Set())

    // Get all company contracts
    const {
        data: contracts,
        isLoading: isLoadingContracts,
        error: errorContracts,
        refetch: refetchContracts,
    } = useQuery<AxiosResponse<ApartmentContract[]>, Error>({
        queryKey: QUERY_KEY,
        queryFn: () => axiosInstance.get('/company/apartment_contract/'),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Get contract analytics
    const { data: analytics, isLoading: isLoadingAnalytics } = useQuery<
        AxiosResponse<ContractAnalytics>,
        Error
    >({
        queryKey: ANALYTICS_QUERY_KEY,
        queryFn: () =>
            axiosInstance.get('/company/apartment_contract/analytics/'),
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    // Create/upload a new contract. Pass optional apartment_ids to attach to specific properties; omit to attach to current user's apartment only.
    const createContract = useMutation({
        mutationFn: async ({
            file,
            apartment_ids,
            contract_type,
            organization_contract_type,
            organization_certificate_type,
        }: {
            file: File
            apartment_ids?: number[]
            contract_type?: ApartmentContractType
            organization_contract_type?: number | null
            organization_certificate_type?: number | null
        }) => {
            const formData = new FormData()
            formData.append('file', file)
            if (apartment_ids != null && apartment_ids.length > 0) {
                formData.append('apartment_ids', JSON.stringify(apartment_ids))
            }
            if (contract_type) {
                formData.append('contract_type', contract_type)
            }
            if (organization_contract_type) {
                formData.append(
                    'organization_contract_type',
                    String(organization_contract_type),
                )
            }
            if (organization_certificate_type) {
                formData.append(
                    'organization_certificate_type',
                    String(organization_certificate_type),
                )
            }

            return axiosInstance.post<ApartmentContract>(
                '/company/apartment_contract/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract uploaded successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to upload contract'
            toast.error(errorMessage)
        },
    })

    // Upload multiple contracts. Optionally pass apartment_ids to attach to specific properties; omit to attach to current apartment only.
    const uploadContracts = async (
        files: File[],
        apartment_ids?: number[],
        contract_type?: ApartmentContractType,
        organization_contract_type?: number | null,
        organization_certificate_type?: number | null,
    ) => {
        const results = await Promise.allSettled(
            files.map((file) =>
                createContract.mutateAsync({
                    file,
                    apartment_ids,
                    contract_type,
                    organization_contract_type,
                    organization_certificate_type,
                }),
            ),
        )

        const successCount = results.filter((r) => r.status === 'fulfilled')
            .length
        const failCount = results.filter((r) => r.status === 'rejected').length

        if (failCount > 0 && successCount > 0) {
            toast.warning(
                `Uploaded ${successCount} contract(s), ${failCount} failed`,
            )
        } else if (failCount > 0 && successCount === 0) {
            toast.error('Failed to upload contracts')
        }

        return results
    }

    // Process/analyze a contract with AI
    const processContract = useMutation({
        mutationFn: async (contractId: number) => {
            setProcessingIds((prev) => new Set(prev).add(contractId))
            return axiosInstance.post(
                `/company/apartment_contract/${contractId}/process_contract/`,
            )
        },
        onSuccess: (_, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract analyzed successfully')
        },
        onError: (error: any, contractId) => {
            setProcessingIds((prev) => {
                const next = new Set(prev)
                next.delete(contractId)
                return next
            })
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to analyze contract'
            toast.error(errorMessage)
        },
    })

    // Update a contract
    const updateContract = useMutation({
        mutationFn: async ({
            contractId,
            data,
        }: {
            contractId: number
            data: updateContractRequest
        }) => {
            return axiosInstance.patch<ApartmentContract>(
                `/company/apartment_contract/${contractId}/`,
                data,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY })
            queryClient.invalidateQueries({ queryKey: ['contractAlerts'] })
            toast.success('Contract updated successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update contract'
            toast.error(errorMessage)
        },
    })

    // Delete a contract
    const deleteContract = useMutation({
        mutationFn: async (contractId: number) => {
            return axiosInstance.delete(
                `/company/apartment_contract/${contractId}/`,
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY })
            toast.success('Contract deleted successfully')
        },
        onError: (error: any) => {
            const errorMessage =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete contract'
            toast.error(errorMessage)
        },
    })

    return {
        contracts: contracts?.data || [],
        isLoadingContracts,
        errorContracts,
        refetchContracts,
        analytics: analytics?.data || null,
        isLoadingAnalytics,
        createContract,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        isUploading: createContract.isLoading,
        isProcessing: processContract.isLoading,
        isUpdating: updateContract.isLoading,
        isDeleting: deleteContract.isLoading,
        processingIds,
    }
}

export interface updateContractRequest {
    file_name?: string
    vendor?: number
    start_date?: string
    end_date?: string
    payment_amount?: number
    payment_term?: ContractPaymentTerm
    contract_type?: ApartmentContractType
    organization_contract_type?: number | null
    organization_certificate_type?: number | null
    auto_renew?: boolean
    renewal_term_months?: number | null
    notice_lead_days?: number | null
    /** Full list of apartment IDs to link the contract to (replaces current list). */
    apartment_ids?: number[]
}
